import { Box, TextField, Typography, InputAdornment } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../core/theme/theme';

export interface StepCodeNameProps {
  code: string;
  name: string;
  suggestedCode: string;
  isCodeUnique: boolean;
  isCodeValid: boolean;
  onChange: (field: 'code' | 'name', value: string) => void;
  errors: { code?: string; name?: string };
}

export default function StepCodeName({
  code,
  name,
  suggestedCode,
  isCodeUnique,
  isCodeValid,
  onChange,
  errors,
}: StepCodeNameProps) {
  const { t } = useTranslation();

  const codeOk = code.trim() !== '' && isCodeValid && isCodeUnique;
  const codeError = code.trim() !== '' && (!isCodeValid || !isCodeUnique);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 5,
        py: 4,
      }}
    >
      {/* Code */}
      <Box sx={{ width: '100%', maxWidth: 480 }}>
        <Typography variant="h3" sx={{ mb: 2, textAlign: 'center' }}>
          {t('projects.wizard.codeQuestion', "What's the project code?")}
        </Typography>
        <TextField
          value={code}
          onChange={(e) => onChange('code', e.target.value)}
          placeholder={suggestedCode}
          fullWidth
          autoFocus
          error={Boolean(errors.code) || codeError}
          helperText={
            errors.code
              ? errors.code
              : codeError && !isCodeUnique
                ? t('projects.wizard.codeTaken', 'This code is already taken')
                : codeError && !isCodeValid
                  ? t('projects.wizard.codeInvalid', 'Invalid code — expected P + 4 or 5 digits')
                  : code.trim() === ''
                    ? t('projects.wizard.codeSuggested', {
                        code: suggestedCode,
                        defaultValue: 'Auto-suggested: {{code}}',
                      })
                    : t('projects.wizard.codeAvailable', 'Available')
          }
          slotProps={{
            input: {
              endAdornment: code.trim() !== '' ? (
                <InputAdornment position="end">
                  {codeOk && (
                    <CheckCircleIcon sx={{ color: colors.softGreen }} />
                  )}
                  {codeError && <ErrorIcon sx={{ color: colors.red }} />}
                </InputAdornment>
              ) : undefined,
              sx: { fontSize: 20 },
            },
          }}
        />
      </Box>

      {/* Name */}
      <Box sx={{ width: '100%', maxWidth: 480 }}>
        <Typography variant="h3" sx={{ mb: 2, textAlign: 'center' }}>
          {t('projects.wizard.nameQuestion', 'And its name?')}
        </Typography>
        <TextField
          value={name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder={t('projects.wizard.namePlaceholder', 'Project name')}
          fullWidth
          error={Boolean(errors.name)}
          helperText={errors.name}
          slotProps={{
            input: {
              sx: { fontSize: 20 },
            },
          }}
        />
      </Box>
    </Box>
  );
}
